import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Loading } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Send, RotateCcw, Phone, AlertTriangle, CheckCircle2 } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) + " " +
      d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
}

function ParentCard({ item, onSent }) {
  const [draft, setDraft] = useState(item.message);
  const [sending, setSending] = useState(false);
  const hasPhone = !!item.parent_phone;

  const send = async () => {
    if (!draft.trim()) return toast.error("Message is empty.");
    setSending(true);
    try {
      const { data } = await api.post(`/tutor/students/${item.student_id}/send-sms`, { message: draft });
      toast.success(`Text sent to ${item.parent_name || "parent"} 🎉`);
      onSent(item.student_id, data.last_sent);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send the text.");
    } finally { setSending(false); }
  };

  return (
    <div className="ember-card p-5" data-testid={`comm-card-${item.student_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-heading text-lg font-extrabold text-[#1E1B2E]">{item.student_name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#524B6B]">
            <span>Parent: <span className="font-semibold">{item.parent_name || "—"}</span></span>
            {hasPhone ? (
              <span className="inline-flex items-center gap-1 text-[#5B21B6]"><Phone className="h-3.5 w-3.5" />{item.parent_phone}</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[#D97706]"><AlertTriangle className="h-3.5 w-3.5" />No contact on file</span>
            )}
          </div>
        </div>
        {item.last_sent && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2.5 py-1 text-xs font-semibold text-[#059669]">
            <CheckCircle2 className="h-3.5 w-3.5" /> Last sent {timeAgo(item.last_sent)}
          </span>
        )}
      </div>

      <Textarea
        data-testid={`comm-message-${item.student_id}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={9}
        className="mt-3 rounded-xl text-sm leading-relaxed"
      />

      <div className="mt-3 flex items-center justify-between">
        <button
          type="button"
          data-testid={`comm-reset-${item.student_id}`}
          onClick={() => setDraft(item.message)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#948EAB] hover:text-[#5B21B6]"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Reset to auto-draft
        </button>
        {hasPhone ? (
          <Button data-testid={`comm-send-${item.student_id}`} onClick={send} disabled={sending} className="rounded-xl">
            <Send className="mr-1.5 h-4 w-4" /> {sending ? "Sending…" : "Approve & send"}
          </Button>
        ) : (
          <Button asChild variant="outline" className="rounded-xl">
            <Link to="/tutor/payments">Add parent contact</Link>
          </Button>
        )}
      </div>
    </div>
  );
}

export default function Communications() {
  const [data, setData] = useState(null);

  const load = () => api.get("/tutor/communications").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) return <Loading />;

  const withPhone = data.items.filter((i) => i.parent_phone);
  const withoutPhone = data.items.filter((i) => !i.parent_phone);

  const onSent = (sid, lastSent) =>
    setData((d) => ({ ...d, items: d.items.map((i) => (i.student_id === sid ? { ...i, last_sent: lastSent } : i)) }));

  return (
    <div className="space-y-6" data-testid="communications-page">
      <PageHeader
        title="Communications"
        subtitle="Review the auto-drafted fortnightly update for each parent, edit it, then approve and send by SMS."
      />

      {!data.sms_configured && (
        <div className="ember-card flex items-center gap-3 border-l-4 border-[#D97706] p-4" data-testid="sms-not-configured">
          <AlertTriangle className="h-5 w-5 text-[#D97706]" />
          <p className="text-sm text-[#524B6B]">SMS sending isn't configured yet, so messages can't go out.</p>
        </div>
      )}

      {withPhone.length === 0 && (
        <div className="ember-card p-8 text-center" data-testid="comm-empty">
          <MessageSquare className="mx-auto mb-3 h-8 w-8 text-[#C4BBE0]" />
          <p className="text-sm text-[#524B6B]">No parent contacts yet. Add parent phone numbers from the Payments page to start sending updates.</p>
          <Button asChild className="mt-4 rounded-xl"><Link to="/tutor/payments">Go to Payments</Link></Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {withPhone.map((item) => <ParentCard key={item.student_id} item={item} onSent={onSent} />)}
      </div>

      {withoutPhone.length > 0 && (
        <div className="ember-card p-5" data-testid="comm-missing">
          <div className="mb-2 text-sm font-bold text-[#1E1B2E]">Students without a parent contact ({withoutPhone.length})</div>
          <p className="text-xs text-[#948EAB]">Add a parent name & phone on the Payments page to include these families.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {withoutPhone.map((i) => (
              <span key={i.student_id} className="rounded-full bg-[#F5F3FF] px-2.5 py-1 text-xs font-semibold text-[#524B6B]">{i.student_name}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
